<template>
  <el-dialog
    title="新建长篇小说"
    :visible.sync="visibleInner"
    width="820px"
    :close-on-click-modal="false"
    :destroy-on-close="true"
    class="wizard-dialog"
  >
    <div class="wizard">
      <!-- 步骤条 -->
      <div class="wizard-steps">
        <div class="wizard-steps-header">
          <div class="wizard-tip">
            <i class="el-icon-edit-outline" />
            <span v-if="step < totalSteps">还差 <b>{{ totalSteps - step }}</b> 步，你的长篇巨作即将诞生！</span>
            <span v-else>最后一步，马上完成！</span>
          </div>
          <div class="wizard-progress-text">{{ step }} / {{ totalSteps }}</div>
        </div>
        <div class="wizard-step-line">
          <template v-for="n in totalSteps">
            <div
              :key="'dot-' + n"
              class="wizard-step-item"
              :class="{ active: n === step, done: n < step }"
            >
              <div class="wizard-step-dot">
                <i v-if="n < step" class="el-icon-check" />
                <span v-else>{{ n }}</span>
              </div>
            </div>
            <div
              v-if="n < totalSteps"
              :key="'line-' + n"
              class="wizard-step-connector"
              :class="{ done: n < step }"
            />
          </template>
        </div>
      </div>

      <!-- 步骤内容 -->
      <div class="wizard-body">

        <!-- Step 1: 受众与偏好 -->
        <section v-if="step === 1">
          <h3>受众与偏好</h3>
          <p class="sub">选择目标读者群体、阅读偏好与接受尺度</p>

          <div class="section-label">读者性别频道</div>
          <el-row :gutter="12">
            <el-col :span="8" v-for="item in audienceOptions" :key="item.value">
              <div
                class="audience-card"
                :class="{ active: form.audience === item.value }"
                @click="form.audience = item.value"
              >
                <div class="audience-icon">{{ item.icon }}</div>
                <div class="audience-title">{{ item.label }}</div>
                <div class="audience-tags">{{ item.tags }}</div>
              </div>
            </el-col>
          </el-row>

          <div class="section-label" style="margin-top:20px">喜欢什么（可多选）</div>
          <div class="pref-tags">
            <el-tag
              v-for="tag in prefOptions"
              :key="tag"
              :type="form.preferences.includes(tag) ? '' : 'info'"
              :effect="form.preferences.includes(tag) ? 'dark' : 'plain'"
              class="pref-tag"
              @click.native="togglePref(tag)"
            >{{ tag }}</el-tag>
          </div>

          <div class="section-label" style="margin-top:20px">接受尺度</div>
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="level-item">
                <span>爽点密集度</span>
                <el-select v-model="form.audienceLevel.density" size="small" placeholder="选择">
                  <el-option label="低" value="low" />
                  <el-option label="中" value="medium" />
                  <el-option label="高" value="high" />
                </el-select>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="level-item">
                <span>感情线</span>
                <el-switch v-model="form.audienceLevel.romance" />
              </div>
            </el-col>
            <el-col :span="8">
              <div class="level-item">
                <span>虐度</span>
                <el-switch v-model="form.audienceLevel.angst" />
              </div>
            </el-col>
          </el-row>
        </section>

        <!-- Step 2: 核心卖点 -->
        <section v-else-if="step === 2">
          <h3>核心卖点</h3>
          <p class="sub">用公式化方式描述你的故事核心，AI 将基于此生成后续内容</p>

          <div class="formula-row">
            <el-input v-model="formula.protagonist" placeholder="主角身份" size="small" class="formula-input" />
            <span class="formula-sep">+</span>
            <el-input v-model="formula.dilemma" placeholder="困境" size="small" class="formula-input" />
            <span class="formula-sep">+</span>
            <el-input v-model="formula.cheat" placeholder="金手指/能力" size="small" class="formula-input" />
            <span class="formula-sep">+</span>
            <el-input v-model="formula.goal" placeholder="目标" size="small" class="formula-input" />
          </div>

          <div v-if="formulaPreview" class="formula-preview">
            <i class="el-icon-magic-stick" /> {{ formulaPreview }}
          </div>

          <div class="section-label" style="margin-top:16px">自由编辑</div>
          <el-input
            v-model="form.sellingPoint"
            type="textarea"
            :rows="5"
            placeholder="例如：现代社畜穿成古代废柴，靠现代知识逆袭争霸天下。"
          />
          <el-button v-if="formulaPreview && !form.sellingPoint" size="mini" type="primary" plain style="margin-top:8px" @click="form.sellingPoint = formulaPreview">
            使用公式生成的卖点
          </el-button>
        </section>

        <!-- Step 3: 字数与分卷 -->
        <section v-else-if="step === 3">
          <h3>字数与分卷</h3>
          <p class="sub">设定总字数和分卷结构，长篇可拆成多卷，每卷一个小结局</p>

          <el-row :gutter="24">
            <el-col :span="12">
              <div class="section-label">目标字数（万字）</div>
              <el-input-number v-model="form.targetWords" :min="100000" :max="5000000" :step="100000" />
              <div class="hint">
                {{ form.targetWords < 300000 ? '中篇（10-30万字）' : '长篇（30万字以上）' }}
              </div>
            </el-col>
            <el-col :span="12">
              <div class="section-label">分卷数量</div>
              <el-input-number v-model="form.volumeCount" :min="2" :max="20" :step="1" />
              <div class="hint">每卷约 {{ Math.round(form.targetWords / form.volumeCount / 10000) }} 万字</div>
            </el-col>
          </el-row>

          <div class="structure-preview">
            <div class="section-label">结构预览：3幕式</div>
            <div class="structure-acts">
              <div class="act-block act1">
                <div class="act-title">第一幕·开局</div>
                <div class="act-percent">10%-20%</div>
              </div>
              <div class="act-block act2">
                <div class="act-title">第二幕·成长</div>
                <div class="act-percent">60%-70%</div>
              </div>
              <div class="act-block act3">
                <div class="act-title">第三幕·结局</div>
                <div class="act-percent">10%-20%</div>
              </div>
            </div>
          </div>
        </section>

        <!-- Step 4: 世界观与冲突 -->
        <section v-else-if="step === 4">
          <h3>世界观与冲突</h3>
          <p class="sub">AI 根据你的卖点和设定生成世界观与核心冲突，可自由编辑</p>

          <div v-if="aiLoading" class="ai-loading">
            <i class="el-icon-loading" />
            <span>AI 正在构建世界观与冲突体系...</span>
          </div>
          <template v-else>
            <div class="section-label">时代/世界类型</div>
            <el-radio-group v-model="form.era" class="era-group" @change="onEraChange">
              <el-radio-button label="ancient">古代</el-radio-button>
              <el-radio-button label="modern">现代</el-radio-button>
              <el-radio-button label="future">未来</el-radio-button>
              <el-radio-button label="fantasy">架空</el-radio-button>
            </el-radio-group>

            <div class="section-label" style="margin-top:16px">世界观设定</div>
            <el-input
              v-model="form.worldSetting"
              type="textarea"
              :rows="5"
              placeholder="AI 将生成世界观设定..."
            />
            <el-button size="mini" type="primary" plain class="regen-btn" @click="doGenWorldSetting">
              <i class="el-icon-refresh" /> 重新生成世界观
            </el-button>

            <div class="section-label" style="margin-top:16px">核心冲突</div>
            <el-input
              v-model="form.conflictSource"
              type="textarea"
              :rows="4"
              placeholder="AI 将生成核心冲突描述..."
            />
            <el-button size="mini" type="primary" plain class="regen-btn" @click="doGenConflict">
              <i class="el-icon-refresh" /> 重新生成冲突
            </el-button>
          </template>
        </section>

        <!-- Step 5: 角色设定 -->
        <section v-else-if="step === 5">
          <h3>角色设定</h3>
          <p class="sub">AI 已为您设计角色阵容，点击角色标签查看和修改详情</p>

          <div v-if="aiLoading" class="ai-loading">
            <i class="el-icon-loading" />
            <span>AI 正在设计角色阵容...</span>
          </div>
          <template v-else>
            <div class="role-section">
              <div class="role-group" v-for="group in roleGroups" :key="group.key">
                <div class="role-label">
                  {{ group.label }}
                  <el-button size="mini" type="primary" plain @click="openRoleDialog(group.key)">新增</el-button>
                </div>
                <div class="role-tags">
                  <el-tag
                    v-for="(role, index) in form.roles[group.key]"
                    :key="group.key + '-' + index"
                    :type="group.tagType"
                    closable
                    @click.native="editRole(group.key, index)"
                    @close="removeRole(group.key, index)"
                  >{{ role.name || '未命名' }}</el-tag>
                  <span v-if="!form.roles[group.key].length" class="role-empty">暂无</span>
                </div>
              </div>
            </div>
            <el-button size="mini" type="primary" plain class="regen-btn" @click="doGenRoles">
              <i class="el-icon-refresh" /> 重新生成全部角色
            </el-button>
          </template>
        </section>

        <!-- Step 6: 三幕大纲 -->
        <section v-else-if="step === 6">
          <h3>三幕大纲</h3>
          <p class="sub">AI 根据角色和冲突生成三幕结构大纲，每幕 3-5 条关键节点</p>

          <div v-if="aiLoading" class="ai-loading">
            <i class="el-icon-loading" />
            <span>AI 正在编写三幕大纲...</span>
          </div>
          <template v-else>
            <div class="outline-acts">
              <div v-for="act in outlineActs" :key="act.key" class="outline-act">
                <div class="outline-act-header" :class="act.key">
                  <span class="outline-act-title">{{ act.title }}</span>
                  <span class="outline-act-pct">{{ act.percent }}</span>
                </div>
                <div class="outline-points">
                  <div
                    v-for="(point, pi) in form.outline[act.key]"
                    :key="pi"
                    class="outline-point"
                  >
                    <span class="point-num">{{ pi + 1 }}.</span>
                    <el-input
                      v-model="form.outline[act.key][pi]"
                      size="small"
                      class="point-input"
                    />
                    <el-button size="mini" type="text" icon="el-icon-close" @click="removeOutlinePoint(act.key, pi)" />
                  </div>
                  <el-button size="mini" type="text" icon="el-icon-plus" @click="addOutlinePoint(act.key)">添加</el-button>
                </div>
              </div>
            </div>
            <el-button size="mini" type="primary" plain class="regen-btn" @click="doGenOutline">
              <i class="el-icon-refresh" /> 重新生成大纲
            </el-button>
          </template>
        </section>

        <!-- Step 7: 分卷细纲 -->
        <section v-else-if="step === 7">
          <h3>分卷细纲</h3>
          <p class="sub">AI 为每卷生成标题和关键剧情节点，可展开编辑</p>

          <div v-if="aiLoading" class="ai-loading">
            <i class="el-icon-loading" />
            <span>AI 正在编写分卷细纲...</span>
          </div>
          <template v-else>
            <el-collapse v-model="activeVolumes" class="volume-collapse">
              <el-collapse-item
                v-for="(vol, vi) in form.volumeOutlineList"
                :key="vi"
                :name="vi"
              >
                <template slot="title">
                  <span class="vol-title-label">第{{ vol.volume }}卷</span>
                  <el-input
                    v-model="vol.title"
                    size="mini"
                    class="vol-title-input"
                    @click.native.stop
                  />
                </template>
                <div v-if="vol.summary" class="vol-summary">{{ vol.summary }}</div>
                <div class="vol-points">
                  <div v-for="(pt, pi) in vol.points" :key="pi" class="vol-point">
                    <span class="point-num">{{ pi + 1 }}.</span>
                    <el-input v-model="pt.title" size="small" placeholder="章节标题" class="point-title-input" />
                    <el-input v-model="pt.plot" size="small" placeholder="剧情描述" class="point-input" />
                    <el-button size="mini" type="text" icon="el-icon-close" @click="vol.points.splice(pi, 1)" />
                  </div>
                  <el-button size="mini" type="text" icon="el-icon-plus" @click="vol.points.push({ title: '', plot: '' })">添加</el-button>
                </div>
              </el-collapse-item>
            </el-collapse>
            <el-button size="mini" type="primary" plain class="regen-btn" @click="doGenVolumeOutline">
              <i class="el-icon-refresh" /> 重新生成分卷细纲
            </el-button>
          </template>
        </section>

        <!-- Step 8: 作品命名 -->
        <section v-else-if="step === 8">
          <h3>作品命名</h3>
          <p class="sub">AI 按三种策略生成候选标题，点击选用或自行填写</p>

          <el-form label-width="80px">
            <el-form-item label="作者笔名">
              <el-input v-model="form.author" placeholder="请输入您的笔名" />
            </el-form-item>
            <el-form-item label="小说名称">
              <el-input v-model="form.title" placeholder="请输入小说名称" />
            </el-form-item>
          </el-form>

          <div class="title-strategies">
            <div v-if="titlesLoading" class="ai-loading small">
              <i class="el-icon-loading" /><span>AI 正在生成标题建议...</span>
            </div>
            <template v-else>
              <div v-for="(strat, si) in titleStrategies" :key="si" class="strategy-block">
                <div class="strategy-label">
                  <i class="el-icon-magic-stick" /> {{ strat.name }}
                </div>
                <div class="title-suggest-list">
                  <el-tag
                    v-for="(t, ti) in strat.titles"
                    :key="ti"
                    class="title-tag"
                    :class="{ selected: form.title === t }"
                    @click.native="form.title = t"
                  >{{ t }}</el-tag>
                </div>
              </div>
            </template>
            <el-button size="mini" type="text" :loading="titlesLoading" @click="doGenTitles" style="margin-top:8px">
              {{ titlesLoading ? '生成中...' : '换一批' }}
            </el-button>
          </div>
        </section>

        <!-- Step 9: 封面选择 -->
        <section v-else-if="step === 9">
          <h3>选择封面</h3>
          <p class="sub">为您的长篇巨作选择一个精美的封面</p>
          <el-radio-group v-model="form.coverType" class="cover-group">
            <el-radio label="ai">AI 智能生成</el-radio>
            <el-radio label="system">系统封面</el-radio>
            <el-radio label="upload">上传图片</el-radio>
          </el-radio-group>
          <p class="hint">点击"创建作品"即可完成长篇小说项目的创建。AI 将自动根据分卷细纲创建卷和章节结构。</p>
        </section>

      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handlePrev" :disabled="step === 1 || aiLoading">上一步</el-button>
      <el-button
        v-if="step < totalSteps"
        type="primary"
        :loading="aiLoading"
        @click="handleNext"
      >
        {{ aiLoading ? 'AI 生成中...' : '下一步' }}
      </el-button>
      <el-button v-else type="primary" :loading="finishing" @click="handleFinish">
        创建作品
      </el-button>
    </span>

    <!-- 角色编辑弹窗 -->
    <el-dialog
      :title="roleDialog.index === -1 ? '新增角色' : '编辑角色'"
      :visible.sync="roleDialog.visible"
      width="560px"
      append-to-body
    >
      <el-form label-width="90px">
        <el-form-item label="姓名">
          <el-input v-model="roleDialog.form.name" maxlength="10" show-word-limit />
        </el-form-item>
        <el-form-item label="性别">
          <el-input v-model="roleDialog.form.gender" maxlength="4" show-word-limit />
        </el-form-item>
        <el-form-item label="年龄">
          <el-input v-model="roleDialog.form.age" maxlength="8" placeholder="如：18岁" show-word-limit />
        </el-form-item>
        <el-form-item label="身份">
          <el-input v-model="roleDialog.form.identity" maxlength="30" show-word-limit />
        </el-form-item>
        <el-form-item label="性格特点">
          <el-input v-model="roleDialog.form.trait" maxlength="30" show-word-limit />
        </el-form-item>
        <el-form-item v-if="isMainRole(roleDialog.groupKey)" label="最大弱点">
          <el-input v-model="roleDialog.form.weakness" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item v-if="isMainRole(roleDialog.groupKey)" label="金手指/能力">
          <el-input v-model="roleDialog.form.ability" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item v-if="isMainRole(roleDialog.groupKey)" label="最终目标">
          <el-input v-model="roleDialog.form.goal" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item v-if="isVillainRole(roleDialog.groupKey)" label="动机">
          <el-input v-model="roleDialog.form.motivation" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="小传">
          <el-input
            v-model="roleDialog.form.bio"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="roleDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveRole">保存</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { createProject } from '@/api/project';
import { createRole } from '@/api/role';
import { createChapter } from '@/api/chapter';
import {
  generateWorldSetting,
  generateConflict,
  generateLongNovelRoles,
  generateThreeActOutline,
  generateVolumeOutline,
  generateLongNovelTitles
} from '@/api/ai';

const EMPTY_ROLE = { name: '', gender: '', age: '', identity: '', trait: '', bio: '', weakness: '', ability: '', goal: '', motivation: '' };

export default {
  name: 'LongNovelWizard',
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      visibleInner: this.visible,
      totalSteps: 9,
      step: 1,
      finishing: false,
      aiLoading: false,
      titlesLoading: false,
      activeVolumes: [0],
      titleStrategies: [],

      formula: { protagonist: '', dilemma: '', cheat: '', goal: '' },

      form: {
        audience: '',
        preferences: [],
        audienceLevel: { density: 'medium', romance: true, angst: false },
        sellingPoint: '',
        targetWords: 500000,
        volumeCount: 5,
        era: 'fantasy',
        worldSetting: '',
        conflictSource: '',
        roles: {
          main: [],
          antagonist: [],
          boss: [],
          helper: [],
          frenemy: [],
          lover: []
        },
        outline: { act1: [], act2: [], act3: [] },
        volumeOutlineList: [],
        author: '',
        title: '',
        coverType: 'ai'
      },

      roleDialog: {
        visible: false,
        groupKey: 'main',
        index: -1,
        form: { ...EMPTY_ROLE }
      },

      audienceOptions: [
        { value: 'male', icon: '\u2694\ufe0f', label: '男频', tags: '玄幻 · 都市 · 历史' },
        { value: 'female', icon: '\ud83c\udf38', label: '女频', tags: '言情 · 穿越 · 种田' },
        { value: 'all', icon: '\ud83c\udf0d', label: '全性别', tags: '悬疑 · 科幻 · 奇幻' }
      ],
      prefOptions: ['打脸', '逆袭', '权谋', '恋爱', '搞钱', '争霸', '破案'],
      roleGroups: [
        { key: 'main', label: '主角', tagType: '' },
        { key: 'antagonist', label: '直接对手', tagType: 'danger' },
        { key: 'boss', label: '终极反派', tagType: 'danger' },
        { key: 'helper', label: '帮手', tagType: 'success' },
        { key: 'frenemy', label: '亦敌亦友', tagType: 'warning' },
        { key: 'lover', label: '感情线', tagType: 'info' }
      ],
      outlineActs: [
        { key: 'act1', title: '第一幕·开局', percent: '10%-20%' },
        { key: 'act2', title: '第二幕·成长', percent: '60%-70%' },
        { key: 'act3', title: '第三幕·结局', percent: '10%-20%' }
      ]
    };
  },
  computed: {
    formulaPreview() {
      const f = this.formula;
      if (!f.protagonist && !f.dilemma) return '';
      const parts = [f.protagonist, f.dilemma, f.cheat, f.goal].filter(Boolean);
      return parts.join('，') + '。';
    }
  },
  watch: {
    visible(val) {
      this.visibleInner = val;
      if (val) this.reset();
    },
    visibleInner(val) {
      this.$emit('update:visible', val);
    }
  },
  methods: {
    reset() {
      this.step = 1;
      this.finishing = false;
      this.aiLoading = false;
      this.titlesLoading = false;
      this.titleStrategies = [];
      this.activeVolumes = [0];
      this.formula = { protagonist: '', dilemma: '', cheat: '', goal: '' };
      this.form = {
        audience: '',
        preferences: [],
        audienceLevel: { density: 'medium', romance: true, angst: false },
        sellingPoint: '',
        targetWords: 500000,
        volumeCount: 5,
        era: 'fantasy',
        worldSetting: '',
        conflictSource: '',
        roles: { main: [], antagonist: [], boss: [], helper: [], frenemy: [], lover: [] },
        outline: { act1: [], act2: [], act3: [] },
        volumeOutlineList: [],
        author: '',
        title: '',
        coverType: 'ai'
      };
    },

    togglePref(tag) {
      const idx = this.form.preferences.indexOf(tag);
      if (idx >= 0) this.form.preferences.splice(idx, 1);
      else this.form.preferences.push(tag);
    },

    handlePrev() {
      if (this.step > 1) this.step -= 1;
    },

    async handleNext() {
      if (this.step >= this.totalSteps) return;
      const next = this.step + 1;

      if (next === 3 && !this.form.sellingPoint?.trim()) {
        this.$message.warning('请先填写核心卖点');
        return;
      }

      if (next === 4) {
        this.step = next;
        if (!this.form.worldSetting) {
          await this.doGenWorldAndConflict();
        }
      } else if (next === 5) {
        this.step = next;
        if (!this.hasAnyRoles()) {
          await this.doGenRoles();
        }
      } else if (next === 6) {
        this.step = next;
        if (!this.form.outline.act1.length) {
          await this.doGenOutline();
        }
      } else if (next === 7) {
        this.step = next;
        if (!this.form.volumeOutlineList.length) {
          await this.doGenVolumeOutline();
        }
      } else if (next === 8) {
        this.step = next;
        if (!this.titleStrategies.length) {
          await this.doGenTitles();
        }
      } else {
        this.step = next;
      }
    },

    buildAiPayload() {
      return {
        audience: this.form.audience,
        preferences: this.form.preferences,
        audienceLevel: this.form.audienceLevel,
        sellingPoint: this.form.sellingPoint,
        targetWords: this.form.targetWords,
        volumeCount: this.form.volumeCount,
        era: this.form.era,
        worldSetting: this.form.worldSetting,
        conflictSource: this.form.conflictSource,
        rolesJson: JSON.stringify(this.flatRoles()),
        outline: JSON.stringify(this.form.outline),
        volumeOutline: JSON.stringify(this.form.volumeOutlineList)
      };
    },

    flatRoles() {
      const all = [];
      for (const [type, list] of Object.entries(this.form.roles)) {
        list.forEach(r => all.push({ ...r, type }));
      }
      return all;
    },

    hasAnyRoles() {
      return Object.values(this.form.roles).some(arr => arr.length > 0);
    },

    onEraChange() {
      // Optionally regenerate world when era changes
    },

    // ---- AI generation methods ----

    stripMarkdownCodeBlock(str) {
      if (!str) return str;
      return str.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim();
    },

    getAiContent(res) {
      if (res.code !== 200) return null;
      if (res.data && res.data.content) return this.stripMarkdownCodeBlock(res.data.content);
      if (res.data && typeof res.data === 'string') return this.stripMarkdownCodeBlock(res.data);
      if (res.msg && res.msg !== '操作成功') return this.stripMarkdownCodeBlock(res.msg);
      return null;
    },

    async doGenWorldAndConflict() {
      this.aiLoading = true;
      try {
        await this.doGenWorldSetting();
        await this.doGenConflict();
      } finally {
        this.aiLoading = false;
      }
    },

    async doGenWorldSetting() {
      const wasLoading = this.aiLoading;
      if (!wasLoading) this.aiLoading = true;
      try {
        const res = await generateWorldSetting(this.buildAiPayload());
        console.log('[LongNovel] world API response:', JSON.stringify(res));
        const raw = this.getAiContent(res);
        if (raw) {
          this.form.worldSetting = this.parseWorldSettingText(raw);
        }
      } catch (e) {
        console.error('[LongNovel] world generation error:', e);
        this.$message.error('世界观生成失败，请手动填写');
      } finally {
        if (!wasLoading) this.aiLoading = false;
      }
    },

    parseWorldSettingText(raw) {
      try {
        let str = raw;
        const start = str.indexOf('{');
        const end = str.lastIndexOf('}');
        if (start >= 0 && end > start) str = str.substring(start, end + 1);
        const obj = JSON.parse(str);
        const parts = [];
        if (obj.summary) parts.push(obj.summary);
        if (obj.techLevel) parts.push('【科技/文明】' + obj.techLevel);
        if (obj.powerSystem) parts.push('【武力体系】' + obj.powerSystem);
        if (obj.socialHierarchy) parts.push('【社会等级】' + obj.socialHierarchy);
        if (obj.powerStructure) parts.push('【权力结构】' + obj.powerStructure);
        return parts.join('\n\n');
      } catch (e) {
        return raw;
      }
    },

    async doGenConflict() {
      const wasLoading = this.aiLoading;
      if (!wasLoading) this.aiLoading = true;
      try {
        const res = await generateConflict(this.buildAiPayload());
        console.log('[LongNovel] conflict API response:', JSON.stringify(res));
        const raw = this.getAiContent(res);
        if (raw) {
          this.form.conflictSource = this.parseConflictText(raw);
        }
      } catch (e) {
        console.error('[LongNovel] conflict generation error:', e);
        this.$message.error('冲突生成失败，请手动填写');
      } finally {
        if (!wasLoading) this.aiLoading = false;
      }
    },

    parseConflictText(raw) {
      try {
        let str = raw;
        const start = str.indexOf('{');
        const end = str.lastIndexOf('}');
        if (start >= 0 && end > start) str = str.substring(start, end + 1);
        const obj = JSON.parse(str);
        const parts = [];
        if (obj.mainConflict) parts.push('核心矛盾：' + obj.mainConflict);
        if (obj.conflictType) parts.push('冲突类型：' + obj.conflictType);
        if (obj.summary) parts.push(obj.summary);
        if (obj.layers && obj.layers.length) {
          parts.push('冲突层次：\n' + obj.layers.map((l, i) => `${i + 1}. ${l}`).join('\n'));
        }
        return parts.join('\n\n');
      } catch (e) {
        return raw;
      }
    },

    async doGenRoles() {
      this.aiLoading = true;
      try {
        const res = await generateLongNovelRoles(this.buildAiPayload());
        console.log('[LongNovel] roles API response:', JSON.stringify(res));
        const raw = this.getAiContent(res);
        console.log('[LongNovel] roles extracted content:', raw);
        if (raw) {
          this.parseRolesFromAi(raw);
        } else {
          this.$message.error('角色生成返回为空，请重试');
        }
      } catch (e) {
        console.error('[LongNovel] roles generation error:', e);
        this.$message.error('角色生成失败，请手动添加');
      } finally {
        this.aiLoading = false;
      }
    },

    parseRolesFromAi(raw) {
      try {
        let str = raw;
        // strip markdown code blocks if still present
        str = str.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim();

        const start = str.indexOf('[');
        const end = str.lastIndexOf(']');
        if (start < 0 || end <= start) {
          console.warn('[LongNovel] roles: no JSON array found in:', str.substring(0, 500));
          this.$message.error('角色数据格式异常，请重新生成');
          return;
        }
        str = str.substring(start, end + 1);
        console.log('[LongNovel] roles JSON to parse:', str.substring(0, 500));
        const arr = JSON.parse(str);

        if (!Array.isArray(arr) || arr.length === 0) {
          this.$message.error('角色数据为空，请重新生成');
          return;
        }

        const grouped = { main: [], antagonist: [], boss: [], helper: [], frenemy: [], lover: [] };
        arr.forEach(r => {
          const type = r.type || r.roleType || 'helper';
          const key = grouped.hasOwnProperty(type) ? type : 'helper';
          grouped[key].push({
            name: r.name || '',
            gender: r.gender || '',
            age: r.age || '',
            identity: r.identity || '',
            trait: r.trait || r.personality || '',
            bio: r.bio || r.description || '',
            weakness: r.weakness || '',
            ability: r.ability || r.cheat || r.power || '',
            goal: r.goal || r.target || '',
            motivation: r.motivation || r.motive || ''
          });
        });
        console.log('[LongNovel] roles parsed:', JSON.stringify(grouped));
        this.form.roles = grouped;
      } catch (e) {
        console.error('[LongNovel] roles parse error:', e, '\nraw:', raw);
        this.$message.error('角色解析失败，请查看控制台日志');
      }
    },

    async doGenOutline() {
      this.aiLoading = true;
      try {
        const res = await generateThreeActOutline(this.buildAiPayload());
        console.log('[LongNovel] outline API response:', JSON.stringify(res));
        const raw = this.getAiContent(res);
        if (raw) {
          this.parseOutlineFromAi(raw);
        }
      } catch (e) {
        console.error('[LongNovel] outline generation error:', e);
        this.$message.error('大纲生成失败，请手动填写');
      } finally {
        this.aiLoading = false;
      }
    },

    parseOutlineFromAi(raw) {
      try {
        let str = raw;
        const start = str.indexOf('{');
        const end = str.lastIndexOf('}');
        if (start >= 0 && end > start) str = str.substring(start, end + 1);
        const obj = JSON.parse(str);
        this.form.outline = {
          act1: (obj.act1 && obj.act1.points) || [],
          act2: (obj.act2 && obj.act2.points) || [],
          act3: (obj.act3 && obj.act3.points) || []
        };
      } catch (e) {
        this.$message.error('大纲解析失败');
      }
    },

    addOutlinePoint(actKey) {
      this.form.outline[actKey].push('');
    },
    removeOutlinePoint(actKey, index) {
      this.form.outline[actKey].splice(index, 1);
    },

    async doGenVolumeOutline() {
      this.aiLoading = true;
      try {
        const res = await generateVolumeOutline(this.buildAiPayload());
        console.log('[LongNovel] volumes API response:', JSON.stringify(res));
        const raw = this.getAiContent(res);
        if (raw) {
          this.parseVolumeOutlineFromAi(raw);
        }
      } catch (e) {
        console.error('[LongNovel] volumes generation error:', e);
        this.$message.error('分卷细纲生成失败，请手动填写');
      } finally {
        this.aiLoading = false;
      }
    },

    normalizePoint(p) {
      if (typeof p === 'string') return { title: '', plot: p };
      if (p && typeof p === 'object') return { title: p.title || '', plot: p.plot || p.description || p.content || '' };
      return { title: '', plot: '' };
    },

    parseVolumeOutlineFromAi(raw) {
      try {
        let str = raw;
        const start = str.indexOf('[');
        const end = str.lastIndexOf(']');
        if (start >= 0 && end > start) str = str.substring(start, end + 1);
        const arr = JSON.parse(str);
        this.form.volumeOutlineList = arr.map((v, i) => ({
          volume: v.volume || (i + 1),
          title: v.title || ('第' + (i + 1) + '卷'),
          summary: v.summary || '',
          points: Array.isArray(v.points) ? v.points.map(p => this.normalizePoint(p)) : []
        }));
        this.activeVolumes = [0];
      } catch (e) {
        console.error('[LongNovel] volume outline parse error:', e, raw);
        this.$message.error('分卷细纲解析失败');
      }
    },

    async doGenTitles() {
      this.titlesLoading = true;
      try {
        const res = await generateLongNovelTitles(this.buildAiPayload());
        console.log('[LongNovel] titles API response:', JSON.stringify(res));
        const raw = this.getAiContent(res);
        if (raw) {
          this.parseTitlesFromAi(raw);
        }
      } catch (e) {
        console.error('[LongNovel] titles generation error:', e);
      } finally {
        this.titlesLoading = false;
      }
    },

    parseTitlesFromAi(raw) {
      try {
        let str = raw;
        const start = str.indexOf('{');
        const end = str.lastIndexOf('}');
        if (start >= 0 && end > start) str = str.substring(start, end + 1);
        const obj = JSON.parse(str);
        this.titleStrategies = [];
        ['strategy1', 'strategy2', 'strategy3'].forEach(k => {
          if (obj[k]) {
            this.titleStrategies.push({
              name: obj[k].name || k,
              titles: Array.isArray(obj[k].titles) ? obj[k].titles : []
            });
          }
        });
      } catch (e) {
        this.titleStrategies = [];
      }
    },

    // ---- Role dialog ----

    isMainRole(groupKey) {
      return groupKey === 'main';
    },
    isVillainRole(groupKey) {
      return groupKey === 'antagonist' || groupKey === 'boss';
    },

    openRoleDialog(groupKey) {
      this.roleDialog.groupKey = groupKey;
      this.roleDialog.index = -1;
      this.roleDialog.form = { ...EMPTY_ROLE };
      this.roleDialog.visible = true;
    },
    editRole(groupKey, index) {
      this.roleDialog.groupKey = groupKey;
      this.roleDialog.index = index;
      this.roleDialog.form = { ...this.form.roles[groupKey][index] };
      this.roleDialog.visible = true;
    },
    removeRole(groupKey, index) {
      this.form.roles[groupKey].splice(index, 1);
    },
    saveRole() {
      const key = this.roleDialog.groupKey;
      if (this.roleDialog.index === -1) {
        this.form.roles[key].push({ ...this.roleDialog.form });
      } else {
        this.$set(this.form.roles[key], this.roleDialog.index, { ...this.roleDialog.form });
      }
      this.roleDialog.visible = false;
    },

    // ---- Finish ----

    async handleFinish() {
      if (!this.form.title?.trim()) {
        this.$message.warning('请先填写小说名称');
        return;
      }
      this.finishing = true;
      try {
        const projectPayload = {
          title: this.form.title,
          author: this.form.author,
          audience: this.form.audience,
          idea: this.form.sellingPoint,
          targetWords: this.form.targetWords,
          background: this.form.worldSetting,
          plot: JSON.stringify(this.form.outline),
          coverType: this.form.coverType,
          novelType: 'long',
          sellingPoint: this.form.sellingPoint,
          audiencePreferences: JSON.stringify(this.form.preferences),
          audienceLevel: JSON.stringify(this.form.audienceLevel),
          volumeCount: this.form.volumeCount,
          worldSetting: this.form.worldSetting,
          conflictSource: this.form.conflictSource,
          volumeOutline: JSON.stringify(this.form.volumeOutlineList),
          chapterRhythm: JSON.stringify({
            perChapter: '一个小事件 + 一个小爽点/反转 + 结尾钩子',
            per3Chapters: '一次小高潮',
            per10Chapters: '一次大高潮 + 升级',
            per30to50Chapters: '一卷结束，换地图/换阶段'
          })
        };

        console.log('[LongNovel] creating project with payload:', projectPayload);
        const res = await createProject(projectPayload);
        console.log('[LongNovel] createProject response:', res);
        const projectId = res.data && res.data.id;

        if (!projectId) {
          this.$message.error('作品创建失败：未返回作品ID');
          return;
        }

        // 批量保存角色
        const allRoles = this.flatRoles();
        if (allRoles.length) {
          console.log('[LongNovel] creating', allRoles.length, 'roles');
          await Promise.all(
            allRoles.map(role =>
              createRole({
                projectId,
                roleType: role.type,
                name: role.name,
                gender: role.gender,
                age: role.age,
                trait: role.trait,
                identity: role.identity,
                bio: role.bio,
                weakness: role.weakness,
                ability: role.ability,
                goal: role.goal,
                motivation: role.motivation
              })
            )
          );
        }

        // 根据分卷细纲创建卷和章节
        if (this.form.volumeOutlineList.length) {
          console.log('[LongNovel] creating', this.form.volumeOutlineList.length, 'volumes');
          for (const vol of this.form.volumeOutlineList) {
            const volRes = await createChapter({
              projectId,
              parentId: 0,
              title: '第' + vol.volume + '卷 ' + vol.title,
              summary: vol.summary || ''
            });
            const volId = volRes.data && volRes.data.id;
            if (volId && vol.points && vol.points.length) {
              for (let i = 0; i < Math.min(vol.points.length, 3); i++) {
                const pt = vol.points[i] || {};
                const chTitle = pt.title
                  ? ('第' + (i + 1) + '章 ' + pt.title)
                  : ('第' + (i + 1) + '章');
                const chSummary = pt.plot || '';
                await createChapter({
                  projectId,
                  parentId: volId,
                  title: chTitle,
                  summary: chSummary
                });
              }
            }
          }
        }

        this.$message.success('长篇小说项目创建成功！');
        this.$emit('finish');
        this.visibleInner = false;
      } catch (e) {
        console.error('[LongNovel] handleFinish error:', e);
        this.$message.error('创建失败：' + (e.msg || e.message || '请查看控制台'));
      } finally {
        this.finishing = false;
      }
    }
  }
};
</script>

<style scoped>
/* ===== Dialog ===== */
.wizard-dialog ::v-deep .el-dialog {
  border-radius: 16px;
  overflow: hidden;
}
.wizard-dialog ::v-deep .el-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f5f5f5;
  font-size: 16px;
  font-weight: 600;
}
.wizard-dialog ::v-deep .el-dialog__body {
  padding: 20px 24px 8px;
  max-height: 65vh;
  overflow-y: auto;
}
.wizard-dialog ::v-deep .el-dialog__footer {
  padding: 12px 24px 20px;
  border-top: 1px solid #f5f5f5;
}

/* ===== Steps ===== */
.wizard-steps {
  margin-bottom: 24px;
  background: linear-gradient(135deg, #fff5f0 0%, #fff9f7 100%);
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid #ffe8dd;
}
.wizard-steps-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.wizard-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #e8633a;
}
.wizard-tip i { font-size: 14px; }
.wizard-tip b { font-weight: 700; color: #ff7f50; }
.wizard-progress-text {
  font-size: 12px;
  color: #c0b4ae;
  font-weight: 500;
}
.wizard-step-line {
  display: flex;
  align-items: center;
}
.wizard-step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}
.wizard-step-dot {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #e8dbd6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: #c0b4ae;
  background: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.wizard-step-item.active .wizard-step-dot {
  background: linear-gradient(135deg, #ff9972, #ff7f50);
  border-color: #ff7f50;
  color: #fff;
  box-shadow: 0 4px 12px rgba(255,127,80,0.45);
  transform: scale(1.15);
}
.wizard-step-item.done .wizard-step-dot {
  background: linear-gradient(135deg, #ffb99a, #ff9972);
  border-color: #ffb399;
  color: #fff;
}
.wizard-step-item.done .wizard-step-dot i { font-size: 12px; font-weight: 700; }
.wizard-step-connector {
  flex: 1;
  height: 2px;
  background: #edddd6;
  margin: 0 2px;
  border-radius: 1px;
  transition: background 0.3s ease;
}
.wizard-step-connector.done {
  background: linear-gradient(90deg, #ffb399, #ff9972);
}

/* ===== Body ===== */
.wizard-body { min-height: 240px; }
.wizard-body h3 {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.wizard-body .sub {
  margin: 0 0 20px;
  color: #909399;
  font-size: 13px;
  line-height: 1.6;
}
.section-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}

/* ===== AI Loading ===== */
.ai-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 32px 0;
  color: #ff7f50;
  font-size: 14px;
  justify-content: center;
}
.ai-loading.small { padding: 12px 0; font-size: 13px; }
.ai-loading i { font-size: 20px; animation: spin 1s linear infinite; }
.ai-loading.small i { font-size: 14px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ===== Regen Button ===== */
.regen-btn { margin-top: 10px; }

/* ===== Audience Cards ===== */
.audience-card {
  padding: 16px 12px;
  border-radius: 10px;
  border: 2px solid #ebeef5;
  background: #fff;
  cursor: pointer;
  transition: all 0.25s ease;
  text-align: center;
}
.audience-card:hover {
  border-color: #ffcbb5;
  box-shadow: 0 4px 12px rgba(255,127,80,0.12);
}
.audience-card.active {
  border-color: #ff7f50;
  background: linear-gradient(135deg, #fff5f0, #fff);
  box-shadow: 0 6px 20px rgba(255,127,80,0.22);
}
.audience-icon { font-size: 24px; margin-bottom: 6px; }
.audience-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
  color: #303133;
}
.audience-card.active .audience-title { color: #ff7f50; }
.audience-tags { font-size: 12px; color: #909399; }

/* ===== Preference Tags ===== */
.pref-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.pref-tag {
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}
.pref-tag:hover {
  border-color: #ff7f50 !important;
  color: #ff7f50 !important;
}

/* ===== Level ===== */
.level-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
  color: #606266;
}

/* ===== Formula ===== */
.formula-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.formula-input { flex: 1; min-width: 100px; }
.formula-sep {
  font-size: 18px;
  font-weight: 700;
  color: #ff7f50;
  flex-shrink: 0;
}
.formula-preview {
  margin-top: 12px;
  padding: 10px 14px;
  background: #fff5f0;
  border-left: 3px solid #ff7f50;
  border-radius: 0 8px 8px 0;
  color: #e8633a;
  font-size: 13px;
  line-height: 1.6;
}

/* ===== Structure Preview ===== */
.structure-preview { margin-top: 24px; }
.structure-acts {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.act-block {
  border-radius: 8px;
  padding: 14px 12px;
  text-align: center;
}
.act-block.act1 {
  flex: 2;
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  border: 1px solid #a5d6a7;
}
.act-block.act2 {
  flex: 6;
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
  border: 1px solid #ffcc80;
}
.act-block.act3 {
  flex: 2;
  background: linear-gradient(135deg, #fce4ec, #f8bbd0);
  border: 1px solid #f48fb1;
}
.act-title { font-size: 13px; font-weight: 600; color: #333; }
.act-percent { font-size: 11px; color: #888; margin-top: 4px; }

/* ===== Era ===== */
.era-group { margin-bottom: 8px; }

/* ===== Roles ===== */
.role-section { margin-top: 4px; }
.role-group { margin-bottom: 14px; }
.role-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.role-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.role-tags .el-tag {
  cursor: pointer;
  border-radius: 6px;
}
.role-empty { font-size: 12px; color: #c0c4cc; }

/* ===== Outline ===== */
.outline-acts { display: flex; flex-direction: column; gap: 16px; }
.outline-act {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  overflow: hidden;
}
.outline-act-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  font-weight: 600;
  font-size: 13px;
}
.outline-act-header.act1 { background: linear-gradient(90deg, #e8f5e9, #f1f8e9); color: #2e7d32; }
.outline-act-header.act2 { background: linear-gradient(90deg, #fff3e0, #fff8e1); color: #e65100; }
.outline-act-header.act3 { background: linear-gradient(90deg, #fce4ec, #f3e5f5); color: #ad1457; }
.outline-act-title { flex: 1; }
.outline-act-pct { font-size: 11px; opacity: 0.7; }
.outline-points { padding: 10px 14px; }
.outline-point {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.point-num { font-size: 12px; color: #909399; min-width: 18px; }
.point-title-input { width: 120px; flex-shrink: 0; }
.point-input { flex: 1; }

/* ===== Volume Collapse ===== */
.volume-collapse { border: none; }
.volume-collapse ::v-deep .el-collapse-item__header {
  font-size: 14px;
  font-weight: 600;
}
.vol-title-label {
  color: #ff7f50;
  margin-right: 8px;
  flex-shrink: 0;
}
.vol-title-input {
  width: 200px;
}
.vol-summary {
  padding: 0 0 10px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.vol-points { padding-bottom: 4px; }
.vol-point {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

/* ===== Title Strategies ===== */
.title-strategies {
  margin-top: 12px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
}
.strategy-block { margin-bottom: 12px; }
.strategy-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 6px;
}
.strategy-label i { color: #ff7f50; }
.title-suggest-list { display: flex; flex-wrap: wrap; gap: 8px; }
.title-tag {
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}
.title-tag:hover {
  border-color: #ff7f50 !important;
  color: #ff7f50 !important;
  background: #fff5f0 !important;
}
.title-tag.selected {
  border-color: #ff7f50 !important;
  color: #fff !important;
  background: #ff7f50 !important;
}

/* ===== Cover ===== */
.cover-group { margin-top: 8px; }
.hint {
  margin-top: 10px;
  font-size: 12px;
  color: #c0c4cc;
  line-height: 1.6;
}
</style>
